import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CasosSucesso() {
  const { user } = useAuth();
  const [casos, setCasos] = useState([]);
  const [montadoras, setMontadoras] = useState([]);
  const [montadores, setMontadores] = useState([]);
  const [filtroMontadora, setFiltroMontadora] = useState("");
  const [novo, setNovo] = useState(false);
  const [form, setForm] = useState({
    titulo: "", depoimento: "", cliente_nome: "", nota: 5,
    montadora_id: "", montador_id: "", cidade: "", estado: "", tipo_servico: "",
  });
  const [foto, setFoto] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const podeCadastrar = ["admin", "empresa", "montadora"].includes(user?.role);

  const load = async () => {
    const q = filtroMontadora ? `?montadora_id=${filtroMontadora}` : "";
    const r = await api.get(`/casos-sucesso${q}`);
    setCasos(r.data);
  };

  useEffect(() => {
    api.get("/montadoras").then(r => setMontadoras(r.data)).catch(() => {});
    api.get("/montadores").then(r => setMontadores(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [filtroMontadora]);

  const salvar = async () => {
    if (!foto) { toast.error("Selecione a foto"); return; }
    if (!form.titulo || !form.depoimento || !form.cliente_nome) {
      toast.error("Preencha título, depoimento e nome do cliente"); return;
    }
    if (!form.montadora_id && !form.montador_id) {
      toast.error("Vincule a uma Montadora ou Montador"); return;
    }
    setSalvando(true);
    try {
      const fd = new FormData();
      fd.append("foto", foto);
      Object.entries(form).forEach(([k, v]) => { if (v !== "" && v != null) fd.append(k, String(v)); });
      await api.post("/casos-sucesso", fd);
      toast.success("Caso de sucesso publicado");
      setNovo(false); setFoto(null);
      setForm({ titulo: "", depoimento: "", cliente_nome: "", nota: 5,
        montadora_id: "", montador_id: "", cidade: "", estado: "", tipo_servico: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSalvando(false); }
  };

  const remover = async (id) => {
    if (!window.confirm("Remover este caso?")) return;
    await api.delete(`/casos-sucesso/${id}`);
    toast.success("Removido");
    load();
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="label-cap">Comercial · Galeria</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Casos de Sucesso</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Avaliações 4–5★ com foto do resultado final. Exibido na pesquisa de Montadoras.
          </p>
        </div>
        <div className="flex gap-3">
          <Select value={filtroMontadora || "_all"} onValueChange={(v) => setFiltroMontadora(v === "_all" ? "" : v)}>
            <SelectTrigger className="w-[240px]" data-testid="cs-filter-montadora"><SelectValue placeholder="Todas montadoras" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Todas montadoras</SelectItem>
              {montadoras.map(m => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}
            </SelectContent>
          </Select>
          {podeCadastrar && (
            <Button onClick={() => setNovo(true)} className="btn-sharp brand-bg text-white" data-testid="cs-novo">
              + Novo caso
            </Button>
          )}
        </div>
      </div>

      {casos.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center text-sm text-muted-foreground">
          Nenhum caso de sucesso publicado ainda.
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-6">
          {casos.map(c => (
            <div key={c.id} className="border border-border bg-white overflow-hidden" data-testid={`caso-${c.id}`}>
              <div className="aspect-[4/3] bg-secondary overflow-hidden">
                <img src={`${API}/api${c.foto_url.replace("/api","")}`} alt={c.titulo}
                  className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-[#FFCC00] text-sm">{"★".repeat(c.nota)}<span className="text-muted-foreground/40">{"★".repeat(5 - c.nota)}</span></div>
                  {(user?.role === "admin"
                    || (user?.role === "empresa" && c.empresa_id === user.empresa_id)
                    || (user?.role === "montadora" && c.montadora_id === user.montadora_id)) && (
                    <button onClick={() => remover(c.id)} className="text-[10px] text-red-700 underline"
                      data-testid={`caso-del-${c.id}`}>Remover</button>
                  )}
                </div>
                <h3 className="font-heading font-bold leading-tight">{c.titulo}</h3>
                <p className="text-xs text-muted-foreground italic">"{c.depoimento}"</p>
                <div className="text-[11px] text-muted-foreground pt-2 border-t border-border">
                  <strong>{c.cliente_nome}</strong>
                  {c.cidade && ` · ${c.cidade}/${c.estado}`}
                  {c.tipo_servico && ` · ${c.tipo_servico}`}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={novo} onOpenChange={setNovo}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Publicar Caso de Sucesso</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-cap">Foto do resultado final * (até 8MB)</Label>
              <div className="mt-2 border-2 border-dashed border-border p-6 text-center">
                <input type="file" accept="image/*" onChange={(e) => setFoto(e.target.files?.[0])}
                  data-testid="cs-foto" />
                {foto && <div className="mt-2 text-xs font-mono">{foto.name}</div>}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="label-cap">Vincular a Montadora</Label>
                <Select value={form.montadora_id || "_none"} onValueChange={(v) => setForm({ ...form, montadora_id: v === "_none" ? "" : v })}>
                  <SelectTrigger className="mt-1" data-testid="cs-montadora"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">—</SelectItem>
                    {montadoras.map(m => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-cap">ou Montador</Label>
                <Select value={form.montador_id || "_none"} onValueChange={(v) => setForm({ ...form, montador_id: v === "_none" ? "" : v })}>
                  <SelectTrigger className="mt-1" data-testid="cs-montador"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">—</SelectItem>
                    {montadores.map(m => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="label-cap">Título *</Label>
              <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                placeholder="Ex: Montagem completa de quarto planejado" data-testid="cs-titulo" />
            </div>
            <div>
              <Label className="label-cap">Depoimento do cliente *</Label>
              <Textarea value={form.depoimento} onChange={(e) => setForm({ ...form, depoimento: e.target.value })}
                rows={3} placeholder="Trecho do feedback positivo" data-testid="cs-depoimento" />
            </div>
            <div className="grid md:grid-cols-4 gap-3">
              <div>
                <Label className="label-cap">Cliente *</Label>
                <Input value={form.cliente_nome} onChange={(e) => setForm({ ...form, cliente_nome: e.target.value })}
                  placeholder="João S." data-testid="cs-cliente" />
              </div>
              <div>
                <Label className="label-cap">Cidade</Label>
                <Input value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })} data-testid="cs-cidade" />
              </div>
              <div>
                <Label className="label-cap">UF</Label>
                <Input value={form.estado} maxLength={2}
                  onChange={(e) => setForm({ ...form, estado: e.target.value.toUpperCase() })} data-testid="cs-uf" />
              </div>
              <div>
                <Label className="label-cap">Nota</Label>
                <Select value={String(form.nota)} onValueChange={(v) => setForm({ ...form, nota: parseInt(v) })}>
                  <SelectTrigger className="mt-1" data-testid="cs-nota"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 ★</SelectItem>
                    <SelectItem value="4">4 ★</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="label-cap">Tipo de serviço</Label>
              <Input value={form.tipo_servico} onChange={(e) => setForm({ ...form, tipo_servico: e.target.value })}
                placeholder="Ex: Montagem de móveis planejados" data-testid="cs-tipo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovo(false)}>Cancelar</Button>
            <Button onClick={salvar} disabled={salvando} className="brand-bg text-white" data-testid="cs-salvar">
              {salvando ? "Publicando…" : "Publicar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
